import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../components/ui/accordion';
import { Sprout, Recycle, Smartphone, Package, TrendingUp, Users } from 'lucide-react';

const KachurwahiPage = () => {
  const phases = [
    {
      title: 'Phase 1: Foundation & Community Mobilization',
      duration: 'Months 1-3',
      activities: [
        {
          icon: Users,
          title: 'Core Steering Committee',
          description: 'Form a committee with Gramtek Mahila, Sarpanch, progressive farmer, teacher, and youth representative to guide the mission.'
        },
        {
          icon: Users,
          title: 'Samuhik Chintan Meetings',
          description: 'Conduct collective deliberation meetings with villagers to build consensus and ownership of the transformation vision.'
        },
        {
          icon: Sprout,
          title: 'One Acre Pledge',
          description: 'Farmers pledge a small land portion. In return: guaranteed purchase by Gramtek, free organic inputs, and labor support from the collective.'
        }
      ]
    },
    {
      title: 'Phase 2: Operationalizing the Model',
      duration: 'Months 4-12',
      activities: [
        {
          icon: Package,
          title: 'Land Pooling & Crop Planning',
          description: 'Coordinate collective farming with seasonal crop planning based on market demand and local soil conditions.'
        },
        {
          icon: Recycle,
          title: 'Waste-to-Wealth Engine',
          description: 'Implement household waste collection and community composting, turning garbage into gold for organic farming.'
        },
        {
          icon: Smartphone,
          title: 'Gramtek App Enhancement',
          description: 'Launch "Our Village\'s Farm" section with live inventory, pre-orders, transparency dashboard, and agricultural knowledge hub.'
        },
        {
          icon: Sprout,
          title: 'Seed Bank Inauguration',
          description: 'Establish village seed bank with appointed "Beej Palak" (Seed Keeper) to preserve indigenous seeds and ensure seed sovereignty.'
        }
      ]
    },
    {
      title: 'Phase 3: Deepening Roots & Scaling Out',
      duration: 'Year 2 Onwards',
      activities: [
        {
          icon: Package,
          title: 'Value-Added Products',
          description: 'Establish mills for grains, spice powders, and units for pickles/papads to increase profit margins and create year-round employment.'
        },
        {
          icon: TrendingUp,
          title: 'Profit-Sharing Charter',
          description: 'Formalize distribution: percentage to farmer, cooperative operations, and community development fund for shared prosperity.'
        },
        {
          icon: Users,
          title: 'Cluster Model',
          description: 'Implement "One Village, One Product" specialization and form cluster-level cooperative federation for urban market access.'
        },
        {
          icon: TrendingUp,
          title: 'Reinvestment & Growth',
          description: 'Invest profits into drip irrigation, solar pumps, greenhouses, and training centers for sustainable agricultural advancement.'
        }
      ]
    }
  ];

  const challenges = [
    {
      challenge: 'Farmer Skepticism',
      solution: 'Start with a small pilot group of 5-10 farmers. Document and share their success stories visibly. Nothing convinces like a neighbor\'s harvest.'
    },
    {
      challenge: 'Initial Low Yields',
      solution: 'Set realistic expectations. Organic transition takes 2-3 seasons. Provide financial support during the transition period to maintain farmer commitment.'
    },
    {
      challenge: 'Logistics & Transportation',
      solution: 'Partner with local transporters. Utilize existing Gramtek agent networks for last-mile delivery. Start local, then expand to nearby towns.'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: 'url(https://images.pexels.com/photos/20527520/pexels-photo-20527520.jpeg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'brightness(0.4)'
          }}
        />
        <div className="relative z-10 max-w-5xl mx-auto px-6 text-center text-white">
          <h1 className="text-4xl md:text-6xl font-semibold mb-6 leading-tight">
            Self-Reliance Mission
          </h1>
          <p className="text-2xl md:text-3xl mb-4">
            A Holistic Village Transformation Model
          </p>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-4xl font-semibold mb-8 text-[#1E3A8A] text-center">
            Building Self-Reliant Rural Ecosystems
          </h2>
          <div className="prose prose-lg max-w-none">
            <p className="text-xl leading-relaxed text-[#374151] mb-6">
              The Self-Reliance Mission (SRM) is a visionary, multi-phase project designed to create truly self-reliant rural ecosystems. It goes beyond single interventions to address food security, waste management, economic empowerment, and community ownership simultaneously.
            </p>
            <p className="text-xl leading-relaxed text-[#374151] mb-6">
              This mission transforms villages into hubs of sustainable agriculture, collective enterprise, and shared prosperity through a proven implementation framework.
            </p>
            <p className="text-xl leading-relaxed text-[#374151]">
              For public servants, SRM represents an opportunity to leave a lasting legacy—a model village that other constituencies will want to replicate.
            </p>
          </div>
        </div>
      </section>

      {/* Three Phases Timeline */}
      <section className="py-20 bg-[#F9FAFB]">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-4xl font-semibold mb-12 text-[#1E3A8A] text-center">
            The Three-Phase Transformation
          </h2>
          
          <div className="space-y-8">
            {phases.map((phase, phaseIndex) => (
              <div key={phaseIndex} className="bg-white p-8 rounded-lg shadow-lg border-2 border-[#E5E7EB]">
                <div className="flex items-start gap-6 mb-6">
                  <div className="w-16 h-16 rounded-full bg-[#047857] flex items-center justify-center flex-shrink-0 text-white text-2xl font-bold">
                    {phaseIndex + 1}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-3xl font-semibold mb-2 text-[#1E3A8A]">{phase.title}</h3>
                    <p className="text-xl text-[#EA580C] font-medium">{phase.duration}</p>
                  </div>
                </div>
                
                <div className="grid md:grid-cols-2 gap-6 ml-0 md:ml-22">
                  {phase.activities.map((activity, activityIndex) => {
                    const Icon = activity.icon;
                    return (
                      <div key={activityIndex} className="flex gap-4 p-4 border-l-4 border-[#047857] bg-[#F9FAFB]">
                        <Icon className="w-8 h-8 text-[#047857] flex-shrink-0 mt-1" />
                        <div>
                          <h4 className="text-lg font-semibold mb-2 text-[#1E3A8A]">{activity.title}</h4>
                          <p className="text-[#374151] leading-relaxed">{activity.description}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Overcoming Challenges */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-4xl font-semibold mb-4 text-[#1E3A8A] text-center">
            Overcoming Challenges
          </h2>
          <p className="text-xl text-center mb-12 text-[#374151] max-w-3xl mx-auto">
            We've anticipated the obstacles and built practical solutions into the model.
          </p>
          
          <Accordion type="single" collapsible className="w-full">
            {challenges.map((item, index) => (
              <AccordionItem key={index} value={`item-${index}`} className="border-2 border-[#E5E7EB] rounded-lg mb-4 px-6">
                <AccordionTrigger className="text-xl font-semibold text-[#1E3A8A] hover:text-[#EA580C] hover:no-underline">
                  {item.challenge}
                </AccordionTrigger>
                <AccordionContent className="text-lg text-[#374151] leading-relaxed pt-4">
                  <strong className="text-[#047857]">Solution:</strong> {item.solution}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Expected Outcomes */}
      <section className="py-20 bg-[#F9FAFB]">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-4xl font-semibold mb-12 text-[#1E3A8A] text-center">
            Expected Outcomes
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-lg shadow-md border-2 border-transparent hover:border-[#047857] transition-colors duration-300">
              <h3 className="text-2xl font-semibold mb-4 text-[#1E3A8A]">Economic Impact</h3>
              <ul className="space-y-3 text-[#374151]">
                <li className="flex items-start gap-2">
                  <span className="text-[#EA580C] font-bold mt-1">•</span>
                  <span>30-50% increase in farmer income</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#EA580C] font-bold mt-1">•</span>
                  <span>Year-round employment opportunities</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#EA580C] font-bold mt-1">•</span>
                  <span>Reduced migration to cities</span>
                </li>
              </ul>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-md border-2 border-transparent hover:border-[#047857] transition-colors duration-300">
              <h3 className="text-2xl font-semibold mb-4 text-[#1E3A8A]">Environmental Impact</h3>
              <ul className="space-y-3 text-[#374151]">
                <li className="flex items-start gap-2">
                  <span className="text-[#EA580C] font-bold mt-1">•</span>
                  <span>100% organic farming adoption</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#EA580C] font-bold mt-1">•</span>
                  <span>90% reduction in village waste</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#EA580C] font-bold mt-1">•</span>
                  <span>Soil health restoration</span>
                </li>
              </ul>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-md border-2 border-transparent hover:border-[#047857] transition-colors duration-300">
              <h3 className="text-2xl font-semibold mb-4 text-[#1E3A8A]">Social Impact</h3>
              <ul className="space-y-3 text-[#374151]">
                <li className="flex items-start gap-2">
                  <span className="text-[#EA580C] font-bold mt-1">•</span>
                  <span>Stronger community bonds</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#EA580C] font-bold mt-1">•</span>
                  <span>Women's economic empowerment</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#EA580C] font-bold mt-1">•</span>
                  <span>Youth engagement and retention</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-[#047857] to-[#059669] text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-semibold mb-6">
            Create a Model Village in Your Constituency
          </h2>
          <p className="text-xl mb-8">
            The Self-Reliance Mission is more than a program—it's a legacy. Let's build it together.
          </p>
          <Button 
            size="lg"
            className="bg-[#EA580C] hover:bg-[#DC4D09] text-white px-12 py-6 text-xl"
            asChild
          >
            <Link to="/contact">Start the Transformation</Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default KachurwahiPage;